import {
  CSSProperties,
  FC,
  Fragment,
  ReactNode,
  SelectHTMLAttributes,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Menu, Transition } from '@headlessui/react';
import { cva } from 'class-variance-authority';
import { GoTriangleDown } from 'react-icons/go';
import { Checkbox } from 'ui/components/form';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';
import {
  useFloating,
  offset,
  autoUpdate,
  flip,
  type Placement,
  type Strategy,
  useTransitionStyles,
} from '@floating-ui/react';

export type Value = string | number | string[] | number[] | boolean;
export interface OPTIONS_PROPS {
  label: string | ReactNode;
  value: Value;
  isSelected?: boolean;
  disabled?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  className?: string;
  description?: string;
  onClick?: () => void;
  withCheckbox?: boolean;
  style?: CSSProperties;
  optionTextStyle?: CSSProperties;
}

interface PROPS
  extends Omit<
    SelectHTMLAttributes<HTMLSelectElement>,
    'size' | 'onChange' | 'defaultValue' | 'value'
  > {
  size?: SIZE;
  label?: string;
  options: OPTIONS_PROPS[];
  isFilter?: boolean;
  isDateField?: boolean;
  onClear?: () => void;
  optionsClassName?: string;
  selectedClassName?: string;
  withSearch?: boolean;
  onSearch?: (search: string) => void;
  customIcon?: ReactNode;
  leftIcon?: ReactNode;
  onChange?: (opt: OPTIONS_PROPS) => void;
  defaultValue?: Value;
  value?: Value;
  labelClassname?: string;
  placement?: Placement;
  strategy?: Strategy;
  selectedStyles?: CSSProperties;
}

const dropdownButtonClasses = cva(
  [
    'flex items-center justify-between',
    'bg-fill-primary rounded-[6px]',
    'border border-transparent',
    'disabled:bg-fill-secondary disabled:text-disabled',
  ],
  {
    variants: {
      size: {
        [SIZE.Small]: ['text-xs', 'px-2.5 py-2.5', 'w-[140px] h-9'],
        [SIZE.Medium]: ['text-sm', 'px-2.5 py-3', 'w-[180px] h-[44px]'],
        [SIZE.Large]: ['text-[16px]', 'px-3 py-4', 'w-[220px] h-[52px]'],
      },
      isOpen: {
        true: ['border-fill-overlay'],
        false: [],
      },
    },
    defaultVariants: {
      size: SIZE.Small,
      isOpen: false,
    },
  },
);

const optionsContainerClasses = cva(
  [
    'py-5 z-10 bg-fill-tertiary',
    'absolute left-0 origin-top-left z-50',
    'shadow-sm rounded-b-[6px]',
    'space-y-2.5',
    'overflow-y-auto',
  ],
  {
    variants: {
      size: {
        [SIZE.Small]: ['w-[140px]'],
        [SIZE.Medium]: ['w-[180px]'],
        [SIZE.Large]: ['w-[220px]'],
      },
    },
    defaultVariants: {
      size: SIZE.Small,
    },
  },
);

const iconClasses = cva([''], {
  variants: {
    size: {
      [SIZE.Small]: ['h-4 w-5'],
      [SIZE.Medium]: ['h-5 w-6'],
      [SIZE.Large]: ['h-6 w-7'],
    },
  },
  defaultVariants: {
    size: SIZE.Small,
  },
});

const Dropdown: FC<PROPS> = ({
  size,
  className,
  options,
  disabled,
  optionsClassName,
  selectedClassName,
  customIcon,
  leftIcon,
  onChange,
  placeholder,
  defaultValue,
  value,
  labelClassname,
  placement = 'bottom-end',
  strategy = 'fixed',
  selectedStyles,
}) => {
  const [selected, setSelected] = useState<Value | undefined>(defaultValue);

  useEffect(() => {
    if (value) {
      setSelected(value);
    }
  }, [value]);

  const label = useMemo(
    () => options.find((opt) => opt.value === selected)?.label ?? placeholder,
    [options, placeholder, selected],
  );

  const { refs, context, floatingStyles } = useFloating({
    placement,
    strategy,
    middleware: [offset({ mainAxis: 2 }), flip()],
    whileElementsMounted: autoUpdate,
  });

  const { styles } = useTransitionStyles({ ...context, open: true });

  return (
    <div className={cn(className)}>
      <Menu
        as="div"
        ref={refs.setReference}
        className="relative inline-block w-full text-left"
      >
        {({ open: isOpen, close }) => (
          <>
            <Menu.Button
              disabled={disabled}
              className={cn(
                dropdownButtonClasses({ size, isOpen }),
                selectedClassName,
              )}
              type="button"
              style={selectedStyles}
            >
              <div className="flex items-center space-x-3">
                {leftIcon}
                <span className={cn('truncate max-w-4/5', labelClassname)}>
                  {label}
                </span>
              </div>
              {customIcon ? (
                customIcon
              ) : (
                <GoTriangleDown
                  className={cn(iconClasses({ size }))}
                  aria-hidden="true"
                />
              )}
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items
                ref={refs.setFloating}
                style={{
                  ...floatingStyles,
                  ...styles,
                }}
                className={cn(
                  optionsContainerClasses({ size }),
                  optionsClassName,
                )}
              >
                {options.map((option, index) => (
                  <Menu.Item
                    disabled={option.disabled}
                    onClick={() => {
                      setSelected(option?.value);
                      option.onClick?.();
                      onChange?.(option);
                      close();
                    }}
                    className={({ active }) =>
                      cn(
                        'flex gap-x-3',
                        'py-2 px-5 w-full text-left',
                        'disabled:text-disabled',
                        'hover:bg-fill-primary',
                        active && 'bg-fill-primary',
                        !option.disabled && 'hover:text-primary-base',
                        option.isSelected && '!text-primary-base font-semibold',
                        option.className,
                        option.description ? 'pb-0 px-[10px]' : 'text-content',
                      )
                    }
                    as={'button'}
                    type="button"
                    key={index}
                    style={option?.style}
                  >
                    <>
                      {option.withCheckbox && (
                        <Checkbox
                          checked={option.isSelected}
                          className="mt-1"
                        />
                      )}
                      <p>
                        <span
                          className={cn(
                            'flex items-center gap-x-2.5',
                            'text-sm',
                            !option.disabled && 'hover:font-semibold',
                          )}
                          style={option?.optionTextStyle}
                        >
                          {option.iconLeft}
                          <span className="truncate max-w-4/5">
                            {option.label}
                          </span>
                          {option.iconRight}
                        </span>
                        {option?.description && (
                          <span
                            className={cn(
                              'text-xs',
                              option.disabled
                                ? 'text-disabled'
                                : 'text-content',
                            )}
                          >
                            {option.description}
                          </span>
                        )}
                      </p>
                    </>
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
    </div>
  );
};

export default Dropdown;
